import React, { Fragment, useEffect, useState, forwardRef } from "react";
import { useAuth } from "../context/AuthContext";
import AsyncSelect from "react-select/async";

const DropDownFacilitySelection = forwardRef(({
  defaultValue,
  onSelected,
  trust,
  disabled = false,
  placeholderText = "Facilities"
}, ref) => {
  const { authAxios } = useAuth();
  const [selected, setSelected] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [previousTrust, setPreviousTrust] = useState(null);

  // Handle defaultValue changes
  useEffect(() => {
    if (defaultValue === null || defaultValue === undefined) {
      setSelected(null);
      return;
    }
    setSelected({ value: defaultValue, label: defaultValue?.name });
  }, [defaultValue]);

  // Reset selection and reload options when trust actually changes
  useEffect(() => {
    // Check if trust has actually changed
    const trustChanged = previousTrust?.id !== trust?.id;
    
    if (!trustChanged && previousTrust !== null) {
      // Trust hasn't changed, nothing to do
      return;
    }
    
    setPreviousTrust(trust);
    
    if (trust === null) {
      setSelected(null);
      onSelected(null);
    } else if (trustChanged && previousTrust !== null) {
      // Only reset facility selection if trust actually changed (not initial load)
      // Keep any existing selection if it belongs to the new trust
      if (selected && selected.value && selected.value.trustId === trust.id) {
        // Keep selection - it belongs to the new trust
      } else {
        // Clear selection since facility doesn't belong to new trust
        setSelected(null);
        onSelected(null);
      }
    }
  }, [trust]);

  const fetchFacilities = async (search = "") => {
    try {
      var params = {};
      if (trust !== null && trust !== undefined) {
        params["trust_id"] = trust?.id;
      }
      if (search !== null && search !== "") {
        params["search"] = search;
      }
      
      const config = {
        headers: {
          "content-type": "application/json",
        },
        params: params,
      };

      const response = await authAxios().post(
        "/api/backoffice/facilities/all",
        null,
        config,
      );
      
      // Map facilities to react-select format
      const options = response.data.map(facility => ({
        value: facility,
        label: facility.name
      }));
      
      return options;
    } catch (error) {
      console.log("Error fetching facilities");
      console.log(error);
      console.log(error.stack);
    }
    return [];
  };

  const selectFacility = (facilityOption) => {
    setSelected(facilityOption);
    onSelected(facilityOption?.value);
  };

  // Add dependency on trust to ensure options are reloaded when trust changes
  const promiseOptions = (inputValue) =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve(fetchFacilities(inputValue));
      }, 300);
    });

  // Expose methods to the parent component via ref
  React.useImperativeHandle(ref, () => ({
    reset: () => {
      setSelected(null);
      onSelected(null);
    }
  }));

  // If trust is not selected, disable the facility dropdown
  const isDisabled = disabled || trust === null;

  return (
    <Fragment>
      <AsyncSelect
        key={trust ? `trust-${trust.id}` : 'no-trust'} // Force re-render when trust changes
        className={"fs-input"}
        cacheOptions={false} // Disable caching to always fetch fresh data
        loadOptions={promiseOptions}
        defaultOptions={true}
        isClearable={true}
        onChange={(data) => {
          selectFacility(data);
        }}
        onInputChange={(value) => setInputValue(value)}
        value={selected}
        inputValue={inputValue}
        placeholder={trust === null ? "Select Trust first" : placeholderText}
        isDisabled={isDisabled}
        noOptionsMessage={() => trust === null ? "Select Trust first" : "No facilities found"}
      />
    </Fragment>
  );
});

export default DropDownFacilitySelection;
